<script setup lang="ts">
//#region imports
import { nextTick, ref } from 'vue';
import { cloneDeep } from 'lodash-es';
import type { EmitsToListeners } from '@/types/core';
import type { FilterRecordFilter } from '@/types/dashboard';
import type { DefaultFilterListKeyType } from '@/views/Dashboard/components/DashboardFilter.vue';
//#endregion

//#region types
export interface ReportsFilterParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
interface Props {
  searchData?: FilterRecordFilter,
  group?: DefaultFilterListKeyType
}

interface  Emits {
  'close': [],
  'success': [value: FilterRecordFilter]
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  group: '' as DefaultFilterListKeyType,
  searchData: () => ({}),
});

const emit = defineEmits<Emits>();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref<any>({});

const pSearchData = ref<FilterRecordFilter>({});
const pGroup = ref('' as DefaultFilterListKeyType);
const exclude = ref(false);
const searchValue = ref('');
//#endregion

//#region methods
const open = () => {
  pSearchData.value = cloneDeep(props.searchData);
  pGroup.value = props.group;

  getData();
  isVisible.value = true;
};

const handleClose = () => {
  exclude.value = false;
  searchValue.value = '';
  isVisible.value = false;
  errors.value = {};
  nextTick(() => {
    emit('close');
  });
};

const handleSave = () => {
  pSearchData.value[pGroup.value] = {
    op: exclude.value ? 'notLike' : 'like',
    value: searchValue.value.split('\n').map((item) => item.trim()).filter(String),
  };
  emit('success', pSearchData.value);
  handleClose();
};

function getData() {
  if (pSearchData.value && pSearchData.value[pGroup.value]) {
    exclude.value = pSearchData.value[pGroup.value]!.op === 'notLike';
    searchValue.value = (pSearchData.value[pGroup.value]!.value as string[]).join('\n');
  } else {
    exclude.value = false;
    searchValue.value = '';
  }
}
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="popup-inside-filter reports-filter-popup"
    :title="`${$t('reports.other.search_by.title')} ${$t(`reports.other.search_by.${pGroup}`)}`"
    :is-loading="isLoading"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-8">
        <UiTextarea
          v-model="searchValue"
          :label="$t(`reports.other.search_by.text.${pGroup}`)"
          :rows="6"
          is-clear
          :placeholder="$t(`reports.other.search_by.placeholder.${group}`)"
        />
        <UiCheckbox
          v-model="exclude"
          :label=" $t('reports.other.exclude')"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          :disabled="searchValue.split('\n').map(item => item.trim()).filter(String).length === 0"
          @click="handleSave"
        >
          {{ $t('reports.other.confirm') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
